import { Link, useLocation } from 'react-router-dom'
import { useLanguageStore } from '../../store/languageStore'
import { useThemeStore } from '../../store/themeStore'
import { 
  FiHome, 
  FiFileText, 
  FiShoppingCart, 
  FiBarChart, 
  FiSettings,
  FiUsers,
  FiBox,
  FiBook,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { menuItems, MenuItem } from '../../data/menuItems'
import { useMenuStore } from '../../store/menuStore'
import { useModuleStore } from '../../store/moduleStore'
import { filterMenuItems, isModuleAllowed } from '../../utils/moduleHelpers'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  TouchSensor,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface FooterItem {
  id: string
  path: string
  icon: any
  label: { ar: string; en: string; fr: string }
}

const defaultFooterItems: FooterItem[] = [
  {
    id: 'stores',
    path: '/stores',
    icon: FiBox,
    label: { ar: 'المخازن', en: 'Stores', fr: 'Entrepôts' }
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    icon: FiHome,
    label: { ar: 'لوحة التحكم', en: 'Dashboard', fr: 'Tableau de Bord' }
  },
  {
    id: 'invoices',
    path: '/invoices',
    icon: FiFileText,
    label: { ar: 'الفواتير', en: 'Invoices', fr: 'Factures' }
  },
  {
    id: 'pos',
    path: '/pos',
    icon: FiShoppingCart,
    label: { ar: 'نقطة البيع', en: 'POS', fr: 'Point de Vente' }
  },
  {
    id: 'customers',
    path: '/customers',
    icon: FiUsers,
    label: { ar: 'العملاء', en: 'Customers', fr: 'Clients' }
  },
  {
    id: 'chart-of-accounts',
    path: '/chart-of-accounts',
    icon: FiBook,
    label: { ar: 'دليل الحسابات', en: 'Accounts', fr: 'Comptes' }
  },
  {
    id: 'reports',
    path: '/reports',
    icon: FiBarChart,
    label: { ar: 'التقارير', en: 'Reports', fr: 'Rapports' }
  },
  {
    id: 'settings',
    path: '/settings',
    icon: FiSettings,
    label: { ar: 'الإعدادات', en: 'Settings', fr: 'Paramètres' }
  },
]

// Sortable Wrapper for Footer Items
function SortableFooterItem({ 
  id, 
  children, 
  isCustomizing 
}: { 
  id: string; 
  children: React.ReactNode; 
  isCustomizing: boolean 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 100 : 1,
    cursor: isCustomizing ? 'grab' : 'pointer',
  }

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...(isCustomizing ? { ...attributes, ...listeners } : {})}
      className={`${isCustomizing ? 'animate-jiggle' : ''}`}
    >
      {children}
    </div>
  )
}

interface FooterProps {
  isVisible?: boolean
  onToggleVisibility?: (visible: boolean) => void
}

export default function Footer({ isVisible: controlledVisible, onToggleVisibility }: FooterProps = {}) {
  const location = useLocation()
  const { language, direction } = useLanguageStore()
  const { theme } = useThemeStore()
  const { 
    footerOrder, setFooterOrder, 
    pinnedItems,
    isCustomizing, setIsCustomizing 
  } = useMenuStore()
  const modules = useModuleStore()
  
  const isRTL = direction === 'rtl'
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  const [internalVisible, setInternalVisible] = useState<boolean>(() => {
    const saved = localStorage.getItem('footer-visible')
    return saved !== null ? saved === 'true' : true
  })

  // Use controlled visibility if provided, otherwise use internal state
  const isVisible = controlledVisible !== undefined ? controlledVisible : internalVisible

  // Only save to localStorage if using internal state
  useEffect(() => {
    if (controlledVisible === undefined) {
      localStorage.setItem('footer-visible', internalVisible.toString())
    }
  }, [internalVisible, controlledVisible])

  const toggleVisibility = () => {
    const newVisible = !isVisible
    if (onToggleVisibility) {
      onToggleVisibility(newVisible)
    } else {
      setInternalVisible(newVisible)
    }
  }
  
  const toggleCustomizing = () => {
    const newVal = !isCustomizing
    setIsCustomizing(newVal)
    if (newVal) {
      toast(isRTL ? 'وضع التخصيص: اسحب أيقونات الفوتر لترتيبها' : 'Customize mode: drag footer icons to reorder', {
        icon: '🔧',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      })
    } else {
      toast.success(isRTL ? 'تم حفظ ترتيب الفوتر' : 'Footer order saved')
    }
  }

  // Sync internal state with controlled visibility
  useEffect(() => {
    if (controlledVisible !== undefined && controlledVisible !== internalVisible) {
      setInternalVisible(controlledVisible)
    }
  }, [controlledVisible, internalVisible])

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Detect if dark theme is active
  const isDarkTheme = theme === 'dark'

  // Flatten menu to map path -> item
  const flatMenu = useMemo(() => {
    const out: Record<string, MenuItem> = {}
    const walk = (nodes: MenuItem[]) => {
      nodes.forEach(n => {
        if (n.path) out[n.path] = n
        if (n.children) walk(n.children)
      })
    }
    walk(filterMenuItems(menuItems, modules))
    return out
  }, [modules])

  const favoriteItems = useMemo(() => {
    return pinnedItems
      .map(p => flatMenu[p])
      .filter(Boolean)
      .filter(item => !item.path || isModuleAllowed(item.path, modules)) as MenuItem[]
  }, [pinnedItems, flatMenu, modules])

  const sortedFooterItems = useMemo(() => {
    if (!footerOrder || footerOrder.length === 0) return defaultFooterItems
    
    return [...defaultFooterItems].sort((a, b) => {
      const aIdx = footerOrder.indexOf(a.id)
      const bIdx = footerOrder.indexOf(b.id)
      
      if (aIdx === -1 && bIdx === -1) return 0
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
  }, [footerOrder])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortedFooterItems.findIndex(i => i.id === active.id)
      const newIndex = sortedFooterItems.findIndex(i => i.id === over.id)
      
      const newItems = arrayMove(sortedFooterItems, oldIndex, newIndex)
      setFooterOrder(newItems.map(i => i.id))
    }
  }

  if (!isVisible) {
    // Show a small toggle button when footer is hidden
    return (
      <div
        className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-30 mb-2"
        dir={direction}
      >
        <button
          onClick={toggleVisibility}
          className="p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          style={{
            background: isDarkTheme
              ? 'rgba(30, 41, 59, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: isDarkTheme
              ? '1px solid rgba(71, 85, 105, 0.3)'
              : '1px solid rgba(226, 232, 240, 0.5)',
            color: isDarkTheme ? 'var(--text-primary, #f1f5f9)' : 'var(--text-primary, #1f2937)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translate(-50%, -4px) scale(1.1)'
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(147, 51, 234, 0.3)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translate(-50%, 0) scale(1)'
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          title={isRTL ? 'إظهار الفوتر' : 'Show Footer'}
        >
          <FiChevronUp size={18} />
        </button>
      </div>
    )
  }

  return (
    <footer
      className="footer-container fixed bottom-0 left-0 right-0 z-30 px-3 transition-all duration-300 overflow-visible pointer-events-none"
      dir={direction}
      style={{
        height: '80px', // Increased height to allow icons to pop up
      }}
    >
      {/* Visual Bar Background */}
      <div 
        className="absolute bottom-0 left-0 right-0 z-0 transition-all duration-300"
        style={{
          height: '60px', // The actual bar height
          background: isDarkTheme
            ? 'rgba(30, 41, 59, 0.85)' 
            : 'rgba(255, 255, 255, 0.85)', 
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderTop: isDarkTheme
            ? '1px solid rgba(71, 85, 105, 0.3)'
            : '1px solid rgba(226, 232, 240, 0.5)',
          boxShadow: isDarkTheme
            ? '0 -4px 24px rgba(0, 0, 0, 0.4)'
            : '0 -4px 24px rgba(0, 0, 0, 0.08)',
          pointerEvents: 'auto', // Allow interactions with the bar background if needed
        }}
      />

      <div className="max-w-7xl mx-auto relative h-full overflow-visible pointer-events-none">
        {/* Toggle button and Customize button */}
        <div 
          className={`absolute bottom-[30px] z-50 flex items-center gap-2 pointer-events-auto ${
            isRTL ? 'left-2' : 'right-2'
          }`}
          style={{ transform: 'translateY(50%)' }}
        >
          {/* Customize Mode Toggle */}
          <button
            onClick={toggleCustomizing}
            className={`p-1.5 rounded-full transition-all duration-300 shadow-sm ${
              isCustomizing 
                ? 'bg-primary-600 text-white ring-2 ring-primary-300 ring-offset-2 animate-pulse' 
                : isDarkTheme ? 'bg-slate-800/80 text-slate-400' : 'bg-white/80 text-slate-500'
            }`}
            title={isRTL ? (isCustomizing ? 'حفظ الترتيب' : 'تخصيص القائمة') : (isCustomizing ? 'Save Order' : 'Customize Menu')}
          >
            <FiSettings size={14} className={isCustomizing ? 'animate-spin-slow' : ''} />
          </button>

          <button
            onClick={toggleVisibility}
            className={`p-1.5 rounded-full transition-all duration-300 ${
              isDarkTheme
                ? 'rgba(147, 51, 234, 0.2)'
                : 'rgba(147, 51, 234, 0.1)'
            }`}
            style={{
              background: isDarkTheme
                ? 'rgba(147, 51, 234, 0.2)'
                : 'rgba(147, 51, 234, 0.1)',
              color: 'rgb(147, 51, 234)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDarkTheme
                ? 'rgba(147, 51, 234, 0.3)'
                : 'rgba(147, 51, 234, 0.2)'
              e.currentTarget.style.transform = 'scale(1.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDarkTheme
                ? 'rgba(147, 51, 234, 0.2)'
                : 'rgba(147, 51, 234, 0.1)'
              e.currentTarget.style.transform = 'scale(1)'
            }}
            title={isRTL ? 'إخفاء الفوتر' : 'Hide Footer'}
          >
            <FiChevronDown size={14} />
          </button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedFooterItems.map(i => i.id)}
            strategy={horizontalListSortingStrategy}
          >
            <nav
              className={`flex items-end justify-center gap-1 md:gap-2 h-full pb-2 overflow-visible pointer-events-none ${isRTL ? 'flex-row-reverse pr-20 pl-4' : 'flex-row pl-20 pr-4'}`}
            >
              {favoriteItems.length > 0 && favoriteItems.map((fav, index) => {
                const Icon = (fav.icon as any) || FiFileText
                const label = fav.label[language] || fav.label.en || fav.label.ar
                const active = isActive(fav.path!)
                const isHovered = hoveredIndex === index
                return (
                  <Link
                    key={`fav-${fav.path}`}
                    to={fav.path!}
                    className={`relative flex flex-col items-center justify-center px-2 md:px-3 py-1 rounded-xl transition-all duration-300 group flex-shrink-0 pointer-events-auto ${isCustomizing ? 'opacity-50 pointer-events-none grayscale' : ''}`}
                    style={{
                      transform: isHovered ? 'translateY(-24px) scale(1.3)' : 'translateY(0) scale(1)',
                      backgroundColor: active
                        ? isDarkTheme
                          ? 'rgba(147, 51, 234, 0.25)'
                          : 'rgba(147, 51, 234, 0.15)'
                        : isHovered
                        ? isDarkTheme
                          ? 'rgba(147, 51, 234, 0.3)'
                          : 'rgba(147, 51, 234, 0.2)'
                        : 'transparent',
                      color: active
                        ? 'rgb(147, 51, 234)'
                        : isDarkTheme
                        ? 'var(--text-primary, #f1f5f9)'
                        : 'var(--text-primary, #1f2937)',
                      zIndex: isHovered ? 100 : active ? 10 : 1,
                      boxShadow: isHovered 
                        ? isDarkTheme 
                          ? '0 15px 30px -5px rgba(0, 0, 0, 0.6)' 
                          : '0 15px 30px -5px rgba(147, 51, 234, 0.4)'
                        : 'none',
                    }}
                    onMouseEnter={() => !isCustomizing && setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    title={label}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:block text-[10px] mt-0.5">{label}</span>
                  </Link>
                )
              })}
              {favoriteItems.length > 0 && (
                <div className="mx-1 w-px h-6 mb-2 flex-shrink-0" style={{ backgroundColor: 'rgba(147, 51, 234, 0.3)' }} />
              )}
              {sortedFooterItems.map((item, index) => {
                const Icon = item.icon
                const label = item.label[language] || item.label.en || item.label.ar
                const active = isActive(item.path)
                const offsetIndex = favoriteItems.length + index
                const isHovered = hoveredIndex === offsetIndex

                return (
                  <SortableFooterItem key={item.id} id={item.id} isCustomizing={isCustomizing}>
                    <Link
                      to={isCustomizing ? '#' : item.path}
                      onClick={(e) => isCustomizing && e.preventDefault()}
                      className={`relative flex flex-col items-center justify-center px-2 md:px-3 py-1 rounded-xl transition-all duration-300 group flex-shrink-0 pointer-events-auto ${isCustomizing ? 'scale-105' : ''}`}
                      style={{
                        transform: !isCustomizing && isHovered ? 'translateY(-24px) scale(1.3)' : 'translateY(0) scale(1)',
                        backgroundColor: isCustomizing 
                          ? isDarkTheme ? 'rgba(147, 51, 234, 0.15)' : 'rgba(147, 51, 234, 0.05)'
                          : active
                            ? isDarkTheme
                              ? 'rgba(147, 51, 234, 0.25)'
                              : 'rgba(147, 51, 234, 0.15)'
                            : isHovered
                            ? isDarkTheme
                              ? 'rgba(147, 51, 234, 0.3)'
                              : 'rgba(147, 51, 234, 0.2)'
                            : 'transparent',
                        color: active
                          ? 'rgb(147, 51, 234)'
                          : isDarkTheme
                          ? 'var(--text-primary, #f1f5f9)'
                          : 'var(--text-primary, #1f2937)',
                        zIndex: isHovered ? 100 : active ? 10 : 1,
                        boxShadow: !isCustomizing && isHovered 
                          ? isDarkTheme 
                            ? '0 15px 30px -5px rgba(0, 0, 0, 0.6)' 
                            : '0 15px 30px -5px rgba(147, 51, 234, 0.4)'
                          : isCustomizing
                            ? '0 0 10px rgba(147, 51, 234, 0.2)'
                            : 'none',
                        border: isCustomizing ? '1px dashed rgba(147, 51, 234, 0.4)' : 'none',
                      }}
                      onMouseEnter={() => !isCustomizing && setHoveredIndex(offsetIndex)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {/* Customize hint */}
                      {isCustomizing && (
                        <div className="absolute -top-1 -right-1 bg-primary-600 rounded-full p-0.5 text-white">
                          <FiSettings size={8} />
                        </div>
                      )}

                      {/* Glow effect on hover */}
                      {!isCustomizing && isHovered && (
                        <div
                          className="absolute inset-0 rounded-xl opacity-60"
                          style={{
                            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.4) 0%, transparent 70%)',
                            filter: 'blur(10px)',
                            zIndex: -1,
                          }}
                        />
                      )}

                      <div
                        className="relative transition-all duration-300"
                        style={{
                          transform: !isCustomizing && isHovered ? 'scale(1.2) rotate(5deg)' : 'scale(1) rotate(0deg)',
                        }}
                      >
                        <Icon
                          size={20}
                          className="transition-all duration-300"
                          style={{
                            filter: !isCustomizing && isHovered
                              ? 'drop-shadow(0 0 10px rgba(147, 51, 234, 0.6))'
                              : 'none',
                          }}
                        />
                      </div>

                      <span
                        className="hidden sm:block text-xs font-medium mt-1 transition-all duration-300 whitespace-nowrap"
                        style={{
                          opacity: isHovered || active || isCustomizing ? 1 : isDarkTheme ? 0.8 : 0.7,
                          transform: !isCustomizing && isHovered ? 'translateY(-2px)' : 'translateY(0)',
                          fontSize: isHovered ? '10px' : '9px',
                          fontWeight: active ? 600 : 500,
                        }}
                      >
                        {label}
                      </span>

                      {active && !isCustomizing && (
                        <div
                          className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: 'rgb(147, 51, 234)',
                            boxShadow: '0 0 8px rgba(147, 51, 234, 0.8)',
                          }}
                        />
                      )}
                    </Link>
                  </SortableFooterItem>
                )
              })}
            </nav>
          </SortableContext>
        </DndContext>
      </div>
    </footer>
  )
}
